import Input, { states } from 'components/core/Input';
/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select, { models as selectModels } from 'components/core/Select';
import hasPermission, { permissions } from 'utils/hasPermission';

import Checkbox from 'components/core/Checkbox';
import EmailSelector from 'components/layout/EmailSelector';
import { GET_USERS_QUERY } from 'pages/UserManagement/queries';
import LinkHospitalToPep from 'components/layout/LinkHospitalToPep';
import LinkHospitalsToUser from 'components/layout/LinkHospitalsToUser';
import ModalDialog from 'components/layout/ModalDialog';
import SectionTitle from 'components/core/SectionTitle';
import maskPhoneNumber from 'utils/maskPhoneNumber';
import useMockableLazyQuery from 'services/apollo/hooks/useMockableLazyQuery';
import useMockableMutation from 'services/apollo/hooks/useMockableMutation';
import { useTranslation } from 'react-i18next';
import clearHospitals from './helpers/clearHospitals';
import { formValidation } from './helpers/formValidation';
import getProfessionalIdFeedbackMessage from './helpers/getProfessionalIdFeedbackMessage';
import {
  findRoleByValue,
  getRoleSelectOptions,
  toTranslatedRoleOption,
} from './helpers/roleOptions';
import {
  buildFormSnapshotFromState,
  isFormSyncedWithUser,
} from './helpers/formSnapshot';
import useGetSubmittingState from './hooks/useGetSubmittingState';
import useHandleOneHospital from './hooks/useHandleOneHospital';
import {
  Form,
  HospitalInfo,
  InputContainer,
  WrapperCenter,
} from './styles';
import { DOES_ID_EXIST, REGISTER_USER_MUTATION } from './queries';

function getFieldState(isValid, readonly) {
  if (readonly) return states.disabled;
  return isValid ? states.default : states.error;
}

export default function AddUser({
  emails, hospitals, roles, onSuccess, onSubmittingStateChange,
  user, readonly, emailAlreadyExists, idAlreadyExists, onDirtyChange,
}) {
  const [fullName, setFullName] = useState({ value: '', isValid: true });
  const [userName, setUserName] = useState({ value: '', isValid: true });
  const [userPosition, setUserPosition] = useState({ value: '', isValid: true });
  const [phoneNumber, setPhoneNumber] = useState({ value: '', isValid: true });
  const [email, setEmail] = useState({ value: '', isValid: true });
  const [proxyEmailAdmin, setProxyEmailAdmin] = useState(null);
  const [roleOrchestra, setRoleOrchestra] = useState({ value: '', isValid: true });
  const [roleDiva, setRoleDiva] = useState({ value: '', isValid: true });
  const [userHospitals, setUserHospitals] = useState({ value: [], isValid: readonly === false });
  const [isPicklistValid, setIsPicklistValid] = useState(true);
  const [professionalBoard, setProfessionalBoard] = useState({ value: '', isValid: true });
  const [boardRegistration, setBoardRegistration] = useState({ value: '', isValid: true });
  const [isSysUser, setIsSysUser] = useState(false);
  const [systemUserState, setSystemUserState] = useState({
    showModal: false,
    initialState: false,
  });
  const initialSnapshotRef = useRef(null);
  const needsBaselineCaptureRef = useRef(true);

  const { t } = useTranslation();

  const [sendForm, { data, loading, error }] = useMockableMutation(REGISTER_USER_MUTATION);
  const [doesIdExist, { data: doesIdExistData }] = useMockableLazyQuery(DOES_ID_EXIST);
  const isUpdate = !!user;

  const council = ['COREN', 'CRM', 'CREFITO', 'CRN'];

  const dedupedEmails = useMemo(
    () => Array.from(new Map(emails.map((item) => [item.value, item])).values()),
    [emails],
  );

  const orchestraRoleOptions = useMemo(
    () => getRoleSelectOptions(roles, 'ORCHESTRA', t, readonly ? user?.roleOrchestra : null),
    [roles, t, readonly, user?.roleOrchestra],
  );

  const divaRoleOptions = useMemo(
    () => getRoleSelectOptions(roles, 'DIVA', t, readonly ? user?.roleDiva : null),
    [roles, t, readonly, user?.roleDiva],
  );

  if (data) {
    onDirtyChange?.(false);
    onSuccess(isUpdate);
  }
  const handleEmailChange = useCallback((value) => {
    setEmail((prev) => ({ ...prev, value: value.value }));
    setProxyEmailAdmin(value.proxyEmailAdmin);
  }, []);

  const memoizedSetUserHospitals = useCallback((value) => {
    setUserHospitals((prev) => ({ ...prev, value }));
  }, []);

  const handleInputpepId = useCallback((event) => {
    const cleanHospitals = clearHospitals(hospitals);
    const value = [
      {
        ...cleanHospitals[0],
        pepId: event.target.value,
      },
    ];
    setUserHospitals((prev) => ({ ...prev, value }));
  }, [hospitals]);

  useGetSubmittingState(onSubmittingStateChange, { loading, error, data });
  const { hasOnlyOneHospital } = useHandleOneHospital(hospitals, memoizedSetUserHospitals);

  function handleSubmit() {
    if (readonly) return;

    if (hasOnlyOneHospital) {
      userHospitals.value = userHospitals.value.map((el) => ({ value: el.value, pepId: el.pepId, label: '' }));
    }

    const hospitals = userHospitals.value.map((el) => (
      { value: el.value, label: el.label, pepId: el.pepId }
    ));
    const payload = {
      fullName: fullName.value,
      username: userName.value,
      userPosition: userPosition.value,
      phoneNumber: phoneNumber.value || '',
      email: email.value,
      roleOrchestra: roleOrchestra.value?.value,
      roleDiva: roleDiva.value?.value,
      proxyEmailAdmin,
      userHospitals: hospitals,
      professionalBoard: professionalBoard.value.value,
      boardRegistration: boardRegistration.value,
      isSystemUser: isSysUser,
    };

    setIsPicklistValid(userHospitals.value.length > 0);

    const { isValid, missingFields } = formValidation(payload, hasOnlyOneHospital);

    if (!userName.isValid && !missingFields.includes('userName')) {
      missingFields.push('userName');
    }

    if (!isValid || !userName.isValid) {
      const set = {
        fullName: setFullName,
        userName: setUserName,
        userPosition: setUserPosition,
        phoneNumber: setPhoneNumber,
        email: setEmail,
        userHospitals: setUserHospitals,
        roleOrchestra: setRoleOrchestra,
        roleDiva: setRoleDiva,
        professionalBoard: setProfessionalBoard,
        boardRegistration: setBoardRegistration,
      };
      missingFields.forEach((missingField) => {
        set[missingField]((prev) => ({ ...prev, isValid: false }));
      });
      return;
    }

    sendForm({
      variables: { user: payload, isUpdate },
      refetchQueries: [
        { query: GET_USERS_QUERY },
      ],
    });
  }

  function preSubmit(event) {
    event.preventDefault();
    if (systemUserState.initialState !== isSysUser) {
      setSystemUserState((prev) => ({ ...prev, showModal: true }));
      return;
    }
    handleSubmit();
  }

  function setProfessionalId({ target: { value } }) {
    setUserName({ isValid: true, value: value.toLowerCase() });
    if (idAlreadyExists) {
      idAlreadyExists(false);
    }
  }

  function handleProfessionalIdOnBlur({ target: { value } }) {
    if (!value) return;
    doesIdExist({ variables: { id: value } })
      .then(({ data: d }) => {
        setUserName((prev) => ({ ...prev, isValid: !d?.doesIdExist }));
        if (idAlreadyExists) {
          idAlreadyExists(d?.doesIdExist);
        }
      });
  }

  const defaultSelectedHospitals = useMemo(() => user?.userHospitals, [user]);

  useEffect(() => {
    const {
      fullName,
      username,
      userPosition,
      phoneNumber,
      email,
      proxyEmailAdmin,
      userHospitals,
      isSystemUser,
    } = user ?? {};

    if (!user) {
      needsBaselineCaptureRef.current = true;
      return;
    }

    if (user) {
      setFullName((prev) => ({ ...prev, value: fullName }));
      setUserName((prev) => ({ ...prev, value: username }));
      setUserPosition((prev) => ({ ...prev, value: userPosition }));
      setPhoneNumber((prev) => ({ ...prev, value: phoneNumber || '' }));
      setEmail((prev) => ({ ...prev, value: email }));
      setProxyEmailAdmin(proxyEmailAdmin);

      if (user.roleOrchestra) {
        const userRoleOrchestra = findRoleByValue(roles, user.roleOrchestra);
        setRoleOrchestra({
          value: toTranslatedRoleOption(userRoleOrchestra, user.roleOrchestra, t),
          isValid: true,
        });
      }

      if (user.roleDiva) {
        const userRoleDiva = findRoleByValue(roles, user.roleDiva);
        setRoleDiva({
          value: toTranslatedRoleOption(userRoleDiva, user.roleDiva, t),
          isValid: true,
        });
      }

      setUserHospitals((prev) => ({ ...prev, value: userHospitals }));
      setProfessionalBoard((prev) => ({
        ...prev,
        value: {
          value: user.professionalBoard,
          label: user.professionalBoard,
        },
      }));
      setBoardRegistration((prev) => ({
        ...prev,
        value: user.boardRegistration,
      }));
      setIsSysUser(isSystemUser);
      setSystemUserState((prev) => ({
        ...prev,
        initialState: isSystemUser || false,
      }));
      needsBaselineCaptureRef.current = true;
    }
  }, [user, roles, t]);

  useEffect(() => {
    if (readonly) {
      onDirtyChange?.(false);
      return;
    }

    const formState = {
      fullName,
      userName,
      userPosition,
      phoneNumber,
      email,
      proxyEmailAdmin,
      roleOrchestra,
      roleDiva,
      userHospitals,
      professionalBoard,
      boardRegistration,
      isSysUser,
    };

    if (needsBaselineCaptureRef.current) {
      if (!isFormSyncedWithUser(user, formState)) return;

      needsBaselineCaptureRef.current = false;
      initialSnapshotRef.current = buildFormSnapshotFromState(formState);
      onDirtyChange?.(false);
      return;
    }

    if (initialSnapshotRef.current === null) return;

    const currentSnapshot = buildFormSnapshotFromState(formState);
    onDirtyChange?.(currentSnapshot !== initialSnapshotRef.current);
  }, [
    readonly,
    onDirtyChange,
    fullName,
    userName,
    userPosition,
    phoneNumber,
    email,
    proxyEmailAdmin,
    roleOrchestra,
    roleDiva,
    userHospitals,
    professionalBoard,
    boardRegistration,
    isSysUser,
    user,
  ]);

  function hospitalsInfo() {
    if (readonly) {
      return (
        <>
          <SectionTitle text={t('linkedHospitals')} />
          {userHospitals.value.map((hospital) => (
            <HospitalInfo key={hospital.value}>
              <span>{`${hospital.label} (${hospital.hospitalGroup || ''})`}</span>
              <span>{hospital.address}</span>
            </HospitalInfo>
          ))}
        </>
      );
    }

    return (
      <>
        <SectionTitle text={t('linkHospitals')} />
        <LinkHospitalsToUser
          defaultSelectedItems={defaultSelectedHospitals}
          canHaveMultipleHospitals={roleOrchestra.value?.canHaveMultipleHospitals}
          hospitals={hospitals}
          callback={memoizedSetUserHospitals}
          feedbackMessage={!isPicklistValid && t('feedback.includeAHospital')}
        />
      </>
    );
  }
  return (
    <Form onSubmit={preSubmit} id="addUser">
      <SectionTitle text={t('professionalData')} />
      <InputContainer>
        <Input
          value={fullName.value}
          onChange={(event) => setFullName((prev) => ({ ...prev, value: event.target.value }))}
          label={t('professionalFullName')}
          placeholder={t('fullName')}
          textAlign="left"
          readonly={readonly}
          required
          state={getFieldState(fullName.isValid, readonly)}
          feedbackMessage={!readonly && !fullName.isValid && t('feedback.requiredField')}
          cy="fullName"
        />
        <Input
          value={userName.value}
          onChange={setProfessionalId}
          onBlur={handleProfessionalIdOnBlur}
          label={t('professionalId')}
          placeholder={t('professionalIdSugestion')}
          textAlign="left"
          readonly={readonly || isUpdate}
          required
          state={
            readonly || isUpdate
              ? states.disabled
              : getFieldState(userName.isValid, readonly)
          }
          feedbackMessage={
            !readonly && !isUpdate
              ? t(getProfessionalIdFeedbackMessage(doesIdExistData?.doesIdExist, userName))
              : undefined
          }
          cy="professionalId"
        />
        <Input
          value={userPosition.value}
          onChange={(event) => setUserPosition((prev) => ({ ...prev, value: event.target.value }))}
          label={t('roleOpt')}
          placeholder={t('professionalPosition')}
          textAlign="left"
          readonly={readonly}
          required
          state={getFieldState(userPosition.isValid, readonly)}
          feedbackMessage={!readonly && !userPosition.isValid && t('feedback.requiredField')}
          cy="professionalPosition"
        />
        <Input
          value={maskPhoneNumber(phoneNumber.value)}
          onChange={(event) => setPhoneNumber((prev) => ({ ...prev, value: event.target.value }))}
          label={t('text.typePhoneNumber')}
          placeholder="00 00000-0000"
          textAlign="left"
          type="tel"
          maxLength={13}
          readonly={readonly}
          state={getFieldState(phoneNumber.isValid, readonly)}
          feedbackMessage={!readonly && !phoneNumber.isValid && t('feedback.invalidPhoneNumber')}
          cy="phoneProfessional"
        />
        <EmailSelector
          value={email.value}
          proxyEmailAdmin={proxyEmailAdmin}
          callback={handleEmailChange}
          options={dedupedEmails}
          readonly={readonly}
          feedbackMessage={!email.isValid && t('feedback.requiredField')}
          setEmailExists={(bool) => emailAlreadyExists(bool)}
        />
        <Select
          value={roleOrchestra?.value}
          label={t('permissionLevel')}
          placeholder={t('text.typePermissionLevel')}
          textAlign="left"
          model={selectModels.light}
          zIndex={9}
          isClearable={!readonly}
          options={orchestraRoleOptions}
          onChange={(value) => setRoleOrchestra((prev) => (
            {
              ...prev,
              value,
            }
          ))}
          readonly={readonly}
          feedbackMessage={(!roleOrchestra.isValid && !roleDiva.isValid) && t('feedback.pleaseSelectPermissionLevel')}
          cy="select-permission-level"
        />
        <Select
          value={roleDiva?.value}
          label={t('permissionLevelDiva')}
          placeholder={t('text.typePermissionLevel')}
          textAlign="left"
          model={selectModels.light}
          zIndex={8}
          isClearable={!readonly}
          options={divaRoleOptions}
          onChange={(value) => setRoleDiva((prev) => (
            {
              ...prev,
              value,
            }
          ))}
          readonly={readonly}
          cy="select-permission-level"
        />
        {hasPermission(permissions.deployment.createHospitalGroup) ? (
          <WrapperCenter>
            <Checkbox
              text={t('systemUser')}
              cy="checkbox-user-system"
              isControlled
              checked={isSysUser}
              onChange={(event) => setIsSysUser(event.target.checked)}
              readOnly={readonly}
            />
          </WrapperCenter>
        ) : null}
        <Select
          value={professionalBoard?.value}
          onChange={(value) => setProfessionalBoard((prev) => (
            {
              ...prev,
              value,
            }
          ))}
          label={t('council')}
          placeholder={t('text.typeCouncil')}
          textAlign="left"
          model={selectModels.light}
          zIndex={6}
          options={council.map((c) => ({
            value: c,
            label: c,
          }))}
          required
          feedbackMessage={!professionalBoard.isValid && t('feedback.pleaseSelectCouncil')}
          readonly={readonly}
          cy="select-council"
        />
        <Input
          value={boardRegistration.value}
          onChange={
            (event) => setBoardRegistration((prev) => ({
              ...prev,
              value: event.target.value,
            }))
          }
          state={getFieldState(boardRegistration.isValid, readonly)}
          label={t('professionalRegistry')}
          feedbackMessage={!readonly && !boardRegistration.isValid && t('feedback.pleaseTypeProfessionalRegistry')}
          textAlign="left"
          readonly={readonly}
        />
        {
          hasOnlyOneHospital && (
            <Input
              label={t('pepId')}
              placeholder={t('pepId')}
              textAlign="left"
              value={userHospitals.value[0] ? userHospitals.value[0].pepId : ''}
              onChange={handleInputpepId}
              required
              readonly={readonly}
              state={getFieldState(userHospitals.isValid, readonly)}
              feedbackMessage={!readonly && !userHospitals.isValid && t('feedback.requiredField')}
              cy="pep-id-one-hospital"
            />
          )
        }
      </InputContainer>
      {
        hasOnlyOneHospital ? (
          <>
            <SectionTitle text={t('hospital')} />
            <HospitalInfo>
              <span>{`${hospitals[0].label} (${hospitals[0].hospitalGroup})`}</span>
              <span>{hospitals[0].address}</span>
            </HospitalInfo>
          </>
        ) : (
          <>
            { hospitalsInfo() }
            <SectionTitle text={t('pepId')} />
            <LinkHospitalToPep
              readonly={readonly}
              hospitals={userHospitals.value}
              callback={(memoizedSetUserHospitals)}
              isValid={userHospitals.isValid}
            />
          </>
        )
      }

      <ModalDialog
        show={systemUserState.showModal}
        title={t('attention')}
        text={(systemUserState.initialState !== isSysUser) && isSysUser ? t('systemUser_checked', { username: fullName.value }) : t('systemUser_unchecked', { username: fullName.value })}
        canClose={false}
        onCancel={() => {
          setSystemUserState((prev) => ({ ...prev, showModal: false }));
          setIsSysUser(systemUserState.initialState);
        }}
        onConfirm={() => {
          setSystemUserState((prev) => ({ ...prev, showModal: false }));
          handleSubmit();
        }}
        confirmLabel={t('button.confirm')}
        cancelLabel={t('button.cancel')}
      />
    </Form>
  );
}

AddUser.defaultProps = {
  emails: [],
  hospitals: [],
  roles: [],
  onSuccess: () => { /* */ },
  onSubmittingStateChange: () => { /* */ },
  onDirtyChange: () => { /* */ },
  readonly: false,
};
